import filter from 'lodash/filter';
import first from 'lodash/first';

import {
  AutoReply,
  AutoRepliesConfig,
  AutoReplyMessageTemplate,
  AutoReplyV2,
} from '@app/components/Admin/types';
import { AUTO_REPLY_TYPES } from '@app/constants/user-mgmt';
import { AuthState } from '@app/types/reducer-state';

export const convertToAutoReplyV1Type = (type: string) => {
  if (type === AUTO_REPLY_TYPES.OUT_OF_OFFICE) {
    return AUTO_REPLY_TYPES.GENERAL;
  } else if (type === AUTO_REPLY_TYPES.OFFICE_HOURS) {
    return AUTO_REPLY_TYPES.OUT_OF_OFFICE;
  }
  return type;
};

export const convertToAutoReplyV2Type = (type: string) => {
  if (type === AUTO_REPLY_TYPES.GENERAL) {
    return AUTO_REPLY_TYPES.OUT_OF_OFFICE;
  } else if (type === AUTO_REPLY_TYPES.OUT_OF_OFFICE) {
    return AUTO_REPLY_TYPES.OFFICE_HOURS;
  }
  return type;
};

/**
 * Determines user status based on auto-reply active state.
 * Returns 'away' if the auto-reply is active, 'active' otherwise.
 */
export const whatStatus = (autoReply: { active?: boolean } | undefined) =>
  autoReply?.active ? 'away' : 'active';

export const availabilityStateToStatus = (
  availabilityState?: string | null,
  fallbackAutoReply?: { active?: boolean },
) => {
  if (availabilityState === 'AWAY') return 'away';
  if (availabilityState === 'ACTIVE') return 'active';
  return whatStatus(fallbackAutoReply);
};

export type AutoReplyPayloadInput = {
  id?: string;
  pk?: string;
  active?: boolean;
  title?: string;
  type?: string;
  sendCriteria?: Partial<AutoReply['sendCriteria']>;
  message?: {
    during?: {
      value?: string;
      type?: string;
    };
    after?: {
      value?: string;
      type?: string;
    };
  };
};

export const formatAutoReplyPayload = (autoReply: AutoReplyPayloadInput) => {
  const displayName = autoReply?.title?.trim() || 'Away Message';

  // Transform to schema-conforming payload
  return {
    type: convertToAutoReplyV2Type(autoReply?.type ?? ''),
    active: autoReply?.active,
    message: {
      ...(autoReply?.message?.during?.value && {
        during: {
          body: autoReply?.message?.during?.value,
          displayName,
          tokens: [],
        },
      }),
      ...(autoReply?.message?.after?.value && {
        after: {
          body: autoReply?.message?.after?.value,
          displayName,
          tokens: [],
        },
      }),
    },
    sendCriteria: {
      timeZone: autoReply?.sendCriteria?.timeZone,
      minutesInactiveThreshold:
        autoReply?.sendCriteria?.minutesInactiveThreshold,
      timing: autoReply?.sendCriteria?.timing,
      days: autoReply?.sendCriteria?.days,
    },
  };
};

export const formatAutoReplyResponse = (
  autoReply: AutoRepliesConfig,
  pk: string,
) => {
  if (!autoReply || !autoReply.autoReplies) {
    return [];
  }

  const currentTime = new Date().toISOString();

  const { disableOooMessage, disableOrgAwayMessage, disableAwayMessages } =
    autoReply || {};

  const getIsActive = (type: string) => {
    if (type === AUTO_REPLY_TYPES.OUT_OF_OFFICE) return !disableOooMessage;
    if (type === AUTO_REPLY_TYPES.AWAY_MESSAGE) return !disableOrgAwayMessage;
    return !disableAwayMessages;
  };

  return autoReply.autoReplies.map((reply) => {
    // Transform message structure from v2 to v1
    const transformMessage = (msg: AutoReplyMessageTemplate) => ({
      type: 'STRING',
      value: msg?.body,
    });

    // Get title from message displayName (prefer 'after' if available)
    const title =
      reply?.message?.after?.displayName ||
      reply?.message?.during?.displayName ||
      '';

    // Get id from message (prefer 'after' if available)
    const id = reply?.message?.after?._id || reply?.message?.during?._id || '';

    return {
      sendCriteria: {
        timeZone: reply?.sendCriteria?.timeZone,
        minutesInactiveThreshold: reply?.sendCriteria?.minutesInactiveThreshold,
        days: reply?.sendCriteria?.days,
        timing: reply?.sendCriteria?.timing,
      },
      active: getIsActive(reply?.type ?? ''),
      updatedAt: currentTime,
      createdAt: currentTime,
      message: {
        ...(reply?.message?.during && {
          during: transformMessage(reply.message.during),
        }),
        ...(reply?.message?.after && {
          after: transformMessage(reply.message.after),
        }),
      },
      pk,
      title,
      type: convertToAutoReplyV1Type(reply?.type ?? ''),
      id,
    };
  });
};

export const getAutoReplyPayload = (
  targetUserId: string,
  currentReply: AutoReplyV2 | AutoReply,
) => {
  return [
    targetUserId,
    convertToAutoReplyV2Type((currentReply as AutoReplyV2).type),
  ];
};

type UserState = {
  user: AuthState;
  _id: string;
  id: string;
};

type Action = {
  meta?: { entityIds: string[] };
  payload: AutoRepliesConfig & { availabilityState?: string | null };
};

export const updateAllAutoReplies = (state: UserState, action: Action) => {
  const raw = formatAutoReplyResponse(
    action?.payload,
    `USER#${action?.meta?.entityIds?.[0]}`,
  );
  const list = (Array.isArray(raw) ? raw : []) as Array<{
    type: string;
    active?: boolean;
  }>;
  const generalAutoRepliesList = filter(list, {
    type: AUTO_REPLY_TYPES.GENERAL,
  });
  const oooRepliesList = filter(list, {
    type: AUTO_REPLY_TYPES.OUT_OF_OFFICE,
  });
  return {
    ...state,
    userStatus: availabilityStateToStatus(
      action?.payload?.availabilityState,
      first(generalAutoRepliesList),
    ),
    userAutoReply: {
      generalAutoReplies: { list: [...generalAutoRepliesList] },
      oooReplies: { list: [...oooRepliesList] },
    },
  };
};
